/**
 * modelsAtoms.ts
 *
 * State atoms related to language models and providers
 *
 * Primary responsibilities:
 * - Manage available hosted models
 * - Manage available local models
 * - Track model settings and capabilities
 * - Handle model providers and API keys
 */

import { atom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import { LLM, LLMID, ModelProvider, OpenRouterLLM } from '@/types'
import { VALID_ENV_KEYS } from '@/types/valid-keys'
import { Tables } from '@/supabase/types'

// API key and provider atoms
export const envKeyMapAtom = atom<Record<string, VALID_ENV_KEYS>>({})

// Model availability atoms
export const availableHostedModelsAtom = atom<LLM[]>([])
export const availableLocalModelsAtom = atom<LLM[]>([])
export const availableOpenRouterModelsAtom = atom<OpenRouterLLM[]>([])

// Custom/user-added models
export const customModelsAtom = atom<Tables<'models'>[]>([])

// Combined list of all models from all sources
export const allModelsAtom = atom<LLM[]>([])

// Selected model preferences
export const preferredModelIdAtom = atomWithStorage<LLMID>('preferredModelId', 'losi-omega')

// Helper functions
export const getModelById = (modelId: LLMID, models: LLM[]) => {
  return models.find((model) => model.modelId === modelId)
}

export const getModelByProviderAndId = (
  provider: ModelProvider,
  modelId: string,
  models: LLM[]
) => {
  return models.find(
    (model) =>
      model.provider === provider && (model.modelId === modelId || model.hostedId === modelId)
  )
}

// Function to get available API keys from profile
export const getAvailableAPIKeys = (profile: any): Record<string, string> => {
  if (!profile) return {}

  const keys: Record<string, string> = {}

  if (profile.anthropic_api_key) keys.anthropic = profile.anthropic_api_key
  if (profile.openai_api_key) keys.openai = profile.openai_api_key
  if (profile.google_gemini_api_key) keys.google = profile.google_gemini_api_key
  if (profile.azure_openai_api_key) keys.azureOpenai = profile.azure_openai_api_key
  if (profile.mistral_api_key) keys.mistral = profile.mistral_api_key
  if (profile.groq_api_key) keys.groq = profile.groq_api_key
  if (profile.perplexity_api_key) keys.perplexity = profile.perplexity_api_key
  if (profile.openrouter_api_key) keys.openrouter = profile.openrouter_api_key

  return keys
}
